   // check4dnr/src/pages/Home.js
// src/pages/Home.js

// check4dnr/src/pages/Home.js
// src/pages/Home.js

import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Button, 
  Typography, 
  CircularProgress, 
  Avatar, 
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  CameraAlt, 
  ExpandMore, 
  Security, 
  MedicalServices, 
  CloudUpload,
  CheckCircle,
  ArrowForward
} from '@mui/icons-material';
import CustomCamera from '../components/CustomCamera';
import { authenticateUser, fetchDocuments, GetSavedUserPhoto } from '../services/api';
import DocumentList from '../components/DocumentList';
import { Link } from 'react-router-dom';
import { trackEvent } from '../services/analytics';

function Home() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [showCamera, setShowCamera] = useState(false);
  const [uploadResultMessage, setUploadResultMessage] = useState(
    'EMS - Please capture a photo of the patient to check for Advanced Directives and POLST'
  );
  const [photo, setPhoto] = useState(null);
  const [savedPhoto, setSavedPhoto] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [userData, setUserData] = useState(null); // To store authenticated user data
  const [documents, setDocuments] = useState([]);
  const [documentsStatus, setDocumentsStatus] = useState('');
  const [confidence, setConfidence] = useState(0);

  const handleCapture = async (dataUrl) => {
    // Track camera capture event
    trackEvent('User', 'Camera Capture', 'Home Page');
    
    setPhoto(dataUrl);
    setShowCamera(false);
    setUploadResultMessage('Uploading photo...');
    setIsUploading(true);
    setUserData(null); // Reset previous user data
    setDocuments([]); // Reset previous documents
    setDocumentsStatus('');
    setSavedPhoto(null);
    setConfidence(0);
    try {
      const response = await authenticateUser(dataUrl);
      const responseData = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
      console.log('authenticateUser responseData:', responseData);

      if (responseData.success && responseData.user_data) {
        // Track successful authentication
        trackEvent('User', 'Authentication Success', 'Home Page');
        
        setUserData(responseData.user_data);
        setUploadResultMessage('Photo authenticated successfully!');
        console.log('Cognito GUID:', responseData.user_data.user_guid);
        console.log('Confidence:', responseData.user_data.Confidence);
        setConfidence(responseData.user_data.Confidence);

        // Attempt to fetch the saved user photo
        /*try {
          const photoResponse = await GetSavedUserPhoto(responseData.user_data.user_guid);
          console.log('GetSavedUserPhoto response:', photoResponse);

          if (photoResponse.success && photoResponse.photo) {
            setSavedPhoto(photoResponse.photo); // Set the base64 photo
          } else {
            setUploadResultMessage('Saved User photo not found.');
          }
        } catch (photoError) {
          console.error('Error fetching saved user photo:', photoError);
          // Handle CORS error or other specific errors
          if (photoError.name === 'TypeError') {
            // Likely a CORS error
            setUploadResultMessage('Unable to fetch saved user photo due to a network error.');
          } else {
            setUploadResultMessage('Error fetching user photo.');
          }
        }
          
        */
      
      } else {
        // Track failed authentication
        trackEvent('User', 'Authentication Failed', 'Home Page');
        
        setUploadResultMessage('User not found.');
      }
    } catch (error) {
      // Track error
      trackEvent('Error', 'Authentication Error', error.message || 'Unknown error');
      
      console.error('Error uploading photo:', error);
      setUploadResultMessage('Error uploading photo.');
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    const getDocuments = async () => {
      if (userData && userData.user_guid) {
        setDocumentsStatus('Fetching POLST documents...');
        try {
          const docsResponse = await fetchDocuments(userData.user_guid);
          console.log('docsResponse:', docsResponse);
          console.log('docsResponse.files:', docsResponse.files);
          if (docsResponse.files) {
            // Track successful document fetch
            trackEvent('Documents', 'Documents Fetched', `Count: ${docsResponse.files.length}`);
            
            setDocuments(docsResponse.files);
            setDocumentsStatus('');
          } else {
            // Track no documents found
            trackEvent('Documents', 'No Documents Found', userData.user_guid);
            
            setDocumentsStatus('No POLST documents found.');
          }
          // Fetch the saved user photo
          const photoResponse = await GetSavedUserPhoto(userData.user_guid);
          if (photoResponse.success && photoResponse.photo) {
            setSavedPhoto(photoResponse.photo); // Set the base64 photo
          } else {
            setUploadResultMessage('Saved User photo not found.');
          }


        } catch (error) {
          // Track error
          trackEvent('Error', 'Document Fetch Error', error.message || 'Unknown error');
          
          console.error('Error fetching documents:', error);
          setDocumentsStatus('Error fetching documents.');
        }
      }
    };

    getDocuments();
  }, [userData]);

  // FAQ data
  const faqItems = [
    {
      question: "How does Check4DNR protect my personal information?",
      answer: "We employ advanced encryption and security protocols to ensure your data remains confidential and secure. We are HIPAA compliant and use the latest Amazon web services technology to ensure your security."
    },
    {
      question: "Can EMS personnel access my documents without my consent?",
      answer: "EMS can only access your documents during emergencies when immediate information is crucial for your care by taking a photo of you at that time."
    },
    {
      question: "What does Check4DNR cost?",
      answer: "EMS personnel can Check4DNR's at no cost. Users can create an account for free, however to upload documents you need to buy a premium subscription. The price for a premium subscription is $99 a year."
    },
    {
      question: "How do I update my advance directives on Check4DNR?",
      answer: "Simply log into your account, and upload any documents you want EMS to have access to."
    },
    {
      question: "What types of documents can I store on Check4DNR?",
      answer: "You can store any advance directive documents including POLST forms, DNR orders, living wills, and healthcare power of attorney documents."
    },
    {
      question: "Is Check4DNR available nationwide?",
      answer: "Yes, Check4DNR is available across the United States, providing consistent access to your advance directives wherever you may be."
    }
  ];

  // Testimonial data
  const testimonials = [
    {
      quote: "Check4DNR provided me with the assurance that my medical preferences are known and accessible. It's a relief to know that in an emergency, EMS can quickly find my directives.",
      author: "Jane D.",
      role: "User"
    },
    {
      quote: "Too often when with a patient, we have to either delay care or go against the patients wishes as we cannot find their advanced directives or family members don't have confidence they have the latest version.",
      author: "Cory",
      role: "Firefighter"
    }
  ];

  return (
    <>
      {/* Original Authentication Section - Keep this at the top */}
      <Container maxWidth="sm" sx={{ mt: 5, textAlign: 'center' }}>
        <Typography variant="body1" sx={{ mb: 3 }}>
        
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, fontSize: '1.2rem' }}>
          {uploadResultMessage}
        </Typography>

        {!showCamera && (
          <Button
            variant="contained"
            startIcon={<CameraAlt />}
            onClick={() => {
              // Track camera open event
              trackEvent('User', 'Open Camera', 'Home Page');
              setShowCamera(true);
            }}
            fullWidth
            disabled={isUploading}
          >
            Open Camera
          </Button>
        )}

        {showCamera && (
          <CustomCamera onCapture={handleCapture} onClose={() => setShowCamera(false)} />
        )}

        {isUploading && <CircularProgress sx={{ mt: 2 }} />}

        {photo && (
          <div style={{ marginTop: 20 }}>
            <Typography variant="h6">Captured Image:</Typography>
            <Avatar
              src={photo}
              alt="Captured"
              sx={{ width: 160, height: 160, margin: '0 auto' }}
            />
          </div>
        )}

        {userData && (
          <Paper sx={{ padding: 3, marginTop: 4 }}>
            <Typography variant="h5" gutterBottom>
              User Information Found
            </Typography>
            <Avatar
              src={`data:image/jpeg;base64,${savedPhoto}`}
              alt={`${userData.firstName} ${userData.lastName}`}
              sx={{ width: 160, height: 160, margin: '0 auto' }}
            />
            <Typography variant="h6" sx={{ mt: 2 }}>
              {userData.firstName} {userData.lastName}
              <br />
              Confidence of match: {confidence.toFixed(5) + '%'}
            </Typography>
          </Paper>
        )}

        {userData && (
          <div style={{ marginTop: 40 }}>
            <Typography variant="h5" gutterBottom>
              Available Documents
            </Typography>
            {documentsStatus && (
              <Typography variant="body1" sx={{ mt: 2, color: 'red' }}>
                {documentsStatus}
              </Typography>
            )}
            <DocumentList
              userGuid={userData.user_guid}
              documents={documents}
              showDeleteButton={false}
            />
          </div>
        )}
      </Container>

      {/* New Marketing Sections - Added below the original content */}
      {!userData && (
        <>
          {/* Hero Section */}
          <Box 
            sx={{
              mt: 8,
              py: 8,
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              textAlign: 'center'
            }}
          >
            <Container maxWidth="md">
              <Typography 
                variant="h2" 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '3rem' }
                }}
              >
                Instant Access to Your Advance Directives
              </Typography>
              <Typography 
                variant="h6" 
                component="p" 
                sx={{ 
                  mb: 4,
                  mx: 'auto',
                  maxWidth: '800px',
                  lineHeight: 1.6
                }}
              >
                Check4DNR utilizes cutting-edge facial recognition technology to ensure critical medical documents, like POLST forms, are always accessible when needed. Whether you're an individual securing your future or an EMS professional seeking immediate information, our platform bridges the gap seamlessly.
              </Typography>
              <Button 
                variant="contained" 
                size="large" 
                component={Link} 
                to="/signup"
                sx={{ 
                  py: 1.5, 
                  px: 4, 
                  backgroundColor: 'white',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                  }
                }}
              >
                Get Started
              </Button>
            </Container>
          </Box>

          {/* What We Offer Section */}
          <Box sx={{ py: 8 }}>
            <Container maxWidth="lg">
              <Typography 
                variant="h3" 
                component="h2" 
                align="center" 
                gutterBottom
                sx={{ fontWeight: 600, mb: 6 }}
              >
                Why Check4DNR?
              </Typography>
              
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Card 
                    elevation={2}
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      p: 3,
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                      For Individuals
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircle color="primary" sx={{ mt: 0.5 }} />
                        <Typography variant="body1">
                          Ensure your medical wishes are honored.
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircle color="primary" sx={{ mt: 0.5 }} />
                        <Typography variant="body1">
                          Secure storage and easy updating of your advance directives.
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircle color="primary" sx={{ mt: 0.5 }} />
                        <Typography variant="body1">
                          Peace of mind knowing EMS can access your information instantly and securely.
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircle color="primary" sx={{ mt: 0.5 }} />
                        <Typography variant="body1">
                          Remove the burden from family members at a time of stress.
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card 
                    elevation={2}
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      p: 3,
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                      For EMS Personnel
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircle color="primary" sx={{ mt: 0.5 }} />
                        <Typography variant="body1">
                          Rapid retrieval of patient directives during emergencies.
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircle color="primary" sx={{ mt: 0.5 }} />
                        <Typography variant="body1">
                          Reduce uncertainty with immediate access to critical information.
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircle color="primary" sx={{ mt: 0.5 }} />
                        <Typography variant="body1">
                          Enhance patient care by respecting documented medical wishes.
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircle color="primary" sx={{ mt: 0.5 }} />
                        <Typography variant="body1">
                          Available anytime you have internet connectivity.
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Container>
          </Box>

          {/* How to Get Started Section */}
          <Box sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
            <Container maxWidth="lg">
              <Typography 
                variant="h3" 
                component="h2" 
                align="center" 
                gutterBottom
                sx={{ fontWeight: 600, mb: 2 }}
              >
                Get Started in 3 Easy Steps
              </Typography>
              
              <Grid container spacing={4} sx={{ mt: 4 }}>
                <Grid item xs={12} md={4}>
                  <Card 
                    elevation={2}
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      p: 3,
                      borderRadius: 2
                    }}
                  >
                    <Box 
                      sx={{ 
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 2,
                        fontSize: '1.5rem',
                        fontWeight: 'bold'
                      }}
                    >
                      1
                    </Box>
                    <Typography variant="h5" component="h3" gutterBottom>
                      Sign Up
                    </Typography>
                    <Typography variant="body1">
                      Create your secure account with Check4DNR.
                    </Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card 
                    elevation={2}
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      p: 3,
                      borderRadius: 2
                    }}
                  >
                    <Box 
                      sx={{ 
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 2,
                        fontSize: '1.5rem',
                        fontWeight: 'bold'
                      }}
                    >
                      2
                    </Box>
                    <Typography variant="h5" component="h3" gutterBottom>
                      Take a Selfie
                    </Typography>
                    <Typography variant="body1">
                      Take a well-lit selfie with your phone to link your identity with your documents for instant retrieval.
                    </Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card 
                    elevation={2}
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      p: 3,
                      borderRadius: 2
                    }}
                  >
                    <Box 
                      sx={{ 
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 2,
                        fontSize: '1.5rem',
                        fontWeight: 'bold'
                      }}
                    >
                      3
                    </Box>
                    <Typography variant="h5" component="h3" gutterBottom>
                      Upload Your Documents
                    </Typography>
                    <Typography variant="body1">
                      Add your advance directives, such as POLST forms, to your encrypted and secure folder.
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
              
              <Box sx={{ textAlign: 'center', mt: 6 }}>
                <Button 
                  variant="contained" 
                  size="large" 
                  component={Link} 
                  to="/signup"
                  endIcon={<ArrowForward />}
                  sx={{ 
                    py: 1.5, 
                    px: 4, 
                    borderRadius: '30px'
                  }}
                >
                  Create Your Account
                </Button>
              </Box>
            </Container>
          </Box>

          {/* Testimonials Section */}
          <Box sx={{ py: 8 }}>
            <Container maxWidth="lg">
              <Typography 
                variant="h3" 
                component="h2" 
                align="center" 
                gutterBottom
                sx={{ fontWeight: 600, mb: 6 }}
              >
                What Our Users Say
              </Typography>
              
              <Grid container spacing={4}>
                {testimonials.map((testimonial, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card 
                      elevation={2}
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        p: 4,
                        borderRadius: 2
                      }}
                    >
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          mb: 3, 
                          fontStyle: 'italic',
                          fontSize: '1.1rem',
                          lineHeight: 1.6
                        }}
                      >
                        "{testimonial.quote}"
                      </Typography>
                      <Box sx={{ mt: 'auto' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {testimonial.author}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {testimonial.role}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>

          {/* FAQ Section */}
          <Box sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
            <Container maxWidth="md">
              <Typography 
                variant="h3" 
                component="h2" 
                align="center" 
                gutterBottom
                sx={{ fontWeight: 600, mb: 6 }}
              >
                Frequently Asked Questions
              </Typography>
              
              {faqItems.map((faq, index) => (
                <Accordion 
                  key={index}
                  sx={{ 
                    mb: 2,
                    '&:before': {
                      display: 'none',
                    },
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls={`panel${index}-content`}
                    id={`panel${index}-header`}
                    sx={{ 
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                      }
                    }}
                  >
                    <Typography variant="h6">{faq.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Container>
          </Box>

          {/* Call to Action */}
          <Box 
            sx={{ 
              py: 8, 
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              textAlign: 'center'
            }}
          >
            <Container maxWidth="md">
              <Typography 
                variant="h3" 
                component="h2" 
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                Ready to Secure Your Medical Wishes?
              </Typography>
              <Typography 
                variant="h6" 
                component="p" 
                sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}
              >
                Join thousands who have already taken control of their advance directives with Check4DNR.
              </Typography>
              <Button 
                variant="contained" 
                size="large" 
                component={Link} 
                to="/signup"
                sx={{ 
                  py: 1.5, 
                  px: 4, 
                  fontSize: '1.1rem',
                  borderRadius: '30px',
                  backgroundColor: 'white',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                  }
                }}
              >
                Sign Up Today
              </Button>
            </Container>
          </Box>
        </>
      )}
    </>
  );
}

export default Home;
